'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSSOStore } from '@/lib/sso/store';
import { CheckCircle2, XCircle, Copy, Check, Plus, RefreshCw, Globe } from 'lucide-react';

interface DomainVerificationProps {
  domains: string[];
  verified: boolean;
}

export function DomainVerification({ domains, verified }: DomainVerificationProps) {
  const {
    domainVerification,
    verificationLoading,
    getDomainVerification,
    verifyDomain,
  } = useSSOStore();

  const [newDomain, setNewDomain] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string | null>(domains[0] || null);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (selectedDomain) {
      getDomainVerification(selectedDomain);
    }
  }, [selectedDomain, getDomainVerification]);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerify = async () => {
    if (!selectedDomain) return;

    setVerifying(true);
    setVerificationResult(null);

    try {
      const success = await verifyDomain(selectedDomain);
      setVerificationResult({
        success,
        message: success
          ? 'Domain verified successfully!'
          : 'Domain verification failed. Please check your DNS record.',
      });
    } catch (error) {
      setVerificationResult({
        success: false,
        message: (error as Error).message,
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="size-5" />
            Domain Verification
          </CardTitle>
          <CardDescription>
            Verify ownership of your organization's domains to enable SSO
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Domain List */}
          <div>
            <Label>Configured Domains</Label>
            {domains.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {domains.map((domain) => (
                  <Badge
                    key={domain}
                    variant={selectedDomain === domain ? 'default' : 'outline'}
                    className="cursor-pointer flex items-center gap-1.5"
                    onClick={() => setSelectedDomain(domain)}
                  >
                    {verified ? (
                      <CheckCircle2 className="size-3 text-green-500" />
                    ) : (
                      <XCircle className="size-3 text-muted-foreground" />
                    )}
                    {domain}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">
                No domains configured. Add domains in the SSO configuration.
              </p>
            )}
          </div>

          {/* Verification Instructions */}
          {selectedDomain && domainVerification && !verified && (
            <div className="space-y-4">
              <Alert>
                <AlertDescription>
                  Add a DNS TXT record to verify ownership of <strong>{selectedDomain}</strong>
                </AlertDescription>
              </Alert>

              <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                <h4 className="font-medium">DNS Record Details</h4>

                <div className="space-y-3">
                  <div>
                    <Label className="text-sm text-muted-foreground">Record Type</Label>
                    <p className="font-mono text-sm">{domainVerification.dns_record_type}</p>
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground">Name / Host</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-background p-2 rounded text-sm">
                        {domainVerification.dns_record_name}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopy(domainVerification.dns_record_name)}
                      >
                        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground">Value</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-background p-2 rounded text-sm break-all">
                        {domainVerification.dns_record_value}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCopy(domainVerification.dns_record_value)}
                      >
                        {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground space-y-2">
                  <p>Instructions:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Log in to your DNS provider (e.g., Cloudflare, Route53, GoDaddy)</li>
                    <li>Navigate to DNS settings for {selectedDomain}</li>
                    <li>Add a new TXT record with the values above</li>
                    <li>Wait 5-10 minutes for DNS propagation</li>
                    <li>Click "Verify Domain" below</li>
                  </ol>
                </div>
              </div>

              <Button
                onClick={handleVerify}
                disabled={verifying || verificationLoading}
              >
                {verifying ? (
                  <>
                    <RefreshCw className="size-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-4 mr-2" />
                    Verify Domain
                  </>
                )}
              </Button>

              {verificationResult && (
                <Alert variant={verificationResult.success ? 'default' : 'destructive'}>
                  <AlertDescription>{verificationResult.message}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Verified State */}
          {selectedDomain && verified && (
            <Alert>
              <CheckCircle2 className="size-4 text-green-500" />
              <AlertDescription>
                Domain <strong>{selectedDomain}</strong> has been verified. Users with email addresses
                from this domain can use SSO to sign in.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Security Note */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Why Domain Verification?</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Domain verification proves you own the email domain and prevents unauthorized
            SSO configurations. Only users with verified domain email addresses will be
            able to use SSO. This is a security measure to prevent account takeover attacks.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
